import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { api, apiErrorMessage } from '@shared/api/client';
import { getScopeForm } from '@shared/api/scope-form';
import type { TransactionDetail, TransactionListItem, TransactionStatus } from '@shared/types/api';
import { UploadDocumentDialog } from './upload-document-dialog';
import { ScopeFormDialog } from './scope-form-dialog';

export type UploadKind = 'DRAFT' | 'FINAL' | 'SUPPORTING' | 'REFERENCE';

// Server mirrors this — keep in sync with DocumentsService.assertCanUpload.
//   APPROVED          → only FINAL (draft cycle is closed)
//   FINAL_DOC_READY   → any non-FINAL (support window — addenda, extra drafts,
//                       reference material are allowed while the tx stays open)
//   Earlier work      → DRAFT only (FINAL blocked pre-approval)
export function associateUploadKinds(status: TransactionStatus): UploadKind[] {
  if (status === 'APPROVED' || status === 'CLIENT_CONFIRMED_FINAL' || status === 'DELIVERED')
    return ['FINAL'];
  if (status === 'FINAL_DOC_READY' || status === 'FINAL_RESULT_RECEIVED')
    return ['DRAFT', 'SUPPORTING', 'REFERENCE'];
  const draftStates: TransactionStatus[] = [
    'PAID',
    'PAYMENT_RECEIVED',
    'ASSIGNED',
    'ASSOCIATE_ASSIGNED',
    'ASSOCIATE_ACCEPTED',
    'CLIENT_CONFIRMED',
    'IN_PROGRESS',
    'DRAFT_PREPARATION',
    'CLIENT_DRAFT_CONFIRMATION',
    'REVIEW',
    'SENT_FOR_APPROVAL',
    'REVISION_REQUESTED',
  ];
  return draftStates.includes(status) ? ['DRAFT', 'SUPPORTING'] : [];
}

type OpenUploadDialog = {
  defaultKind: UploadKind;
  availableKinds: UploadKind[];
} | null;

export function AssociateTransactionActions({
  tx,
}: {
  tx: TransactionListItem | TransactionDetail;
}) {
  const qc = useQueryClient();
  const [uploadDialog, setUploadDialog] = useState<OpenUploadDialog>(null);
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const base = `/transactions/${tx.id}`;

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['transactions'] });
    qc.invalidateQueries({ queryKey: ['transaction', tx.id] });
    qc.invalidateQueries({ queryKey: ['chat', tx.id, 'history'] });
  }

  const submitReview = useMutation({
    mutationFn: () => api.post<TransactionDetail>(`${base}/submit-for-review`).then((r) => r.data),
    onSuccess: invalidate,
  });
  const finalReady = useMutation({
    mutationFn: () =>
      api.post<TransactionDetail>(`${base}/mark-final-doc-ready`).then((r) => r.data),
    onSuccess: invalidate,
  });

  const err = submitReview.error ?? finalReady.error;
  const s: TransactionStatus = tx.status;
  const isPaymentPending = !tx.paidAt;
  const pending = submitReview.isPending || finalReady.isPending;

  function openUpload(k: UploadKind) {
    if (isPaymentPending) return;
    const available = associateUploadKinds(s);
    setUploadDialog({
      defaultKind: k,
      availableKinds: available,
    });
  }

  const isStartsFromService = Boolean(tx.service?.isStartsFrom);

  const { data: scopeForm, isLoading: isScopeLoading } = useQuery({
    queryKey: ['scope-form', tx.id],
    queryFn: () => getScopeForm(tx.id),
    enabled: isStartsFromService && tx.associateAssignmentStatus === 'ACCEPTED',
  });

  // Scope form status: hide button once admin has finalized and sent the quotation to client (FINALIZED or ACCEPTED)
  const scopeFormStatus = scopeForm?.status ?? null;
  const isFinalizedOrAccepted = scopeFormStatus === 'FINALIZED' || scopeFormStatus === 'ACCEPTED';
  const isPostQuotationStatus =
    s === 'PAYMENT_PENDING' ||
    s === 'INVOICE_CREATED' ||
    s === 'PAID' ||
    s === 'PAYMENT_RECEIVED' ||
    s === 'IN_PROGRESS' ||
    s === 'DRAFT_PREPARATION' ||
    s === 'CLIENT_DRAFT_CONFIRMATION' ||
    s === 'REVIEW' ||
    s === 'REVISION_REQUESTED' ||
    s === 'SENT_FOR_APPROVAL' ||
    s === 'APPROVED' ||
    s === 'DELIVERED' ||
    s === 'FINAL_DOC_READY' ||
    s === 'COMPLETE' ||
    s === 'CLOSED';

  const actions: React.ReactNode[] = [];

  // Show Scope Form button only when:
  //   - Service is a "Starts From" service (isStartsFrom === true)
  //   - Associate has accepted the assignment
  //   - Scope form query is NOT loading
  //   - Admin has NOT yet finalized quotation and status is not post-quotation
  if (
    isStartsFromService &&
    tx.associateAssignmentStatus === 'ACCEPTED' &&
    !isScopeLoading &&
    !isFinalizedOrAccepted &&
    !isPostQuotationStatus
  ) {
    actions.push(
      <Button
        key="scope-form"
        className="h-7 px-2.5 py-0.5 text-xs bg-emerald-700 hover:bg-emerald-800 text-white font-medium shadow-xs"
        onClick={() => setScopeDialogOpen(true)}
      >
        {scopeForm ? 'Edit Scope & Fee Form' : 'Scope & Fee Form'}
      </Button>,
    );
  }

  const isDraftPhase =
    s === 'ASSOCIATE_ACCEPTED' ||
    s === 'CLIENT_CONFIRMED' ||
    s === 'ASSIGNED' ||
    s === 'IN_PROGRESS' ||
    s === 'DRAFT_PREPARATION' ||
    s === 'REVISION_REQUESTED';

  if (isDraftPhase) {
    actions.push(
      <Button
        key="upload-draft"
        className="h-7 px-2.5 py-0.5 text-xs font-medium"
        variant="outline"
        onClick={() => openUpload('DRAFT')}
        disabled={pending || isPaymentPending}
        title={
          isPaymentPending
            ? 'Payment pending: Admin must create & mark invoice paid first'
            : undefined
        }
      >
        Upload draft
      </Button>,
      <Button
        key="submit-review"
        className="h-7 px-2.5 py-0.5 text-xs bg-brand-700 hover:bg-brand-800 text-white font-medium shadow-xs"
        onClick={() => submitReview.mutate()}
        disabled={pending || isPaymentPending}
        title={
          isPaymentPending
            ? 'Payment pending: Admin must create & mark invoice paid first'
            : undefined
        }
      >
        {submitReview.isPending ? 'Sending…' : 'Send for review'}
      </Button>,
    );
  }

  if (s === 'APPROVED' || s === 'CLIENT_CONFIRMED_FINAL' || s === 'FINAL_RESULT_RECEIVED') {
    actions.push(
      <Button
        key="upload-final"
        className="h-7 px-2.5 py-0.5 text-xs font-medium"
        variant="outline"
        onClick={() => openUpload('FINAL')}
        disabled={pending || isPaymentPending}
        title={
          isPaymentPending
            ? 'Payment pending: Admin must create & mark invoice paid first'
            : undefined
        }
      >
        Upload final
      </Button>,
      <Button
        key="mark-final"
        className="h-7 px-2.5 py-0.5 text-xs bg-brand-700 hover:bg-brand-800 text-white font-medium shadow-xs"
        onClick={() => finalReady.mutate()}
        disabled={pending || isPaymentPending}
        title={
          isPaymentPending
            ? 'Payment pending: Admin must create & mark invoice paid first'
            : undefined
        }
      >
        {finalReady.isPending ? 'Saving…' : 'Mark final ready'}
      </Button>,
    );
  }

  const allowedKinds = associateUploadKinds(s);
  if (allowedKinds.length > 0) {
    const defaultKind: UploadKind = allowedKinds.includes('SUPPORTING')
      ? 'SUPPORTING'
      : allowedKinds[0];
    actions.push(
      <Button
        key="upload-other"
        className="h-7 px-2.5 py-0.5 text-xs font-medium text-gray-700 hover:text-gray-900"
        variant="ghost"
        onClick={() => openUpload(defaultKind)}
        disabled={pending || isPaymentPending}
        title={
          isPaymentPending
            ? 'Payment pending: Admin must create & mark invoice paid first'
            : undefined
        }
      >
        Upload
      </Button>,
    );
  }

  if (actions.length === 0 && !isPaymentPending) {
    return null;
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {actions}
        {isPaymentPending ? (
          <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800 border border-amber-200">
            <svg
              className="h-3 w-3 text-amber-600 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            Payment pending — Admin must create & mark invoice paid to enable draft/review
          </span>
        ) : null}
        {err ? <span className="text-[11px] text-red-600">{apiErrorMessage(err)}</span> : null}
      </div>

      {uploadDialog ? (
        <UploadDocumentDialog
          open
          onClose={() => setUploadDialog(null)}
          transactionId={tx.id}
          defaultKind={uploadDialog.defaultKind}
          availableKinds={uploadDialog.availableKinds}
        />
      ) : null}

      {scopeDialogOpen ? (
        <ScopeFormDialog
          open
          onClose={() => setScopeDialogOpen(false)}
          transactionId={tx.id}
          initialScopeForm={scopeForm}
        />
      ) : null}
    </>
  );
}
